// Srovnání s českou konkurencí — feature matice + ceny. Vyvážený, pravdivý tón.
// Data ověřena z veřejných ceníků k červnu 2026 (ceny se mohou měnit).
// Čeština je základ; překlady doplníme dávkově později.

export type CellStatus = 'yes' | 'partial' | 'no'
export interface Cell {
  s: CellStatus
  t?: string // volitelná poznámka (zobrazí se pod ikonou)
}

export interface Competitor {
  key: string
  name: string
  us?: boolean
  origin: string // CZ/SK/EN
  entryPrice: string // vstupní placený tarif
  freeTier: string
}

export const COMPETITORS: Competitor[] = [
  { key: 'us', name: 'SEO Tools', us: true, origin: 'CZ', entryPrice: '490 Kč', freeTier: '20 analýz/den bez registrace' },
  { key: 'mm', name: 'Marketing Miner', origin: 'CZ/SK', entryPrice: '649 Kč', freeTier: 'Minee (1 000 kreditů)' },
  { key: 'collabim', name: 'Collabim', origin: 'CZ', entryPrice: '598 Kč', freeTier: '150 klíčových slov' },
  { key: 'mangools', name: 'Mangools', origin: 'SK', entryPrice: '~700 Kč', freeTier: 'omezený free' },
]

export interface CompareRow {
  feature: string
  cells: Record<string, Cell>
}

export const COMPARE_ROWS: CompareRow[] = [
  {
    feature: 'Okamžité technické & on-page nástroje (robots, sitemap, schema, hreflang, redirects, DNS/SSL, broken links…)',
    cells: {
      us: { s: 'yes', t: '26 nástrojů' },
      mm: { s: 'partial', t: 'v rámci auditu' },
      collabim: { s: 'partial', t: 'analýza stránek' },
      mangools: { s: 'no' },
    },
  },
  {
    feature: 'Reálný Google Lighthouse + Core Web Vitals (CrUX) + Safe Browsing',
    cells: {
      us: { s: 'yes' },
      mm: { s: 'no' },
      collabim: { s: 'no' },
      mangools: { s: 'no' },
    },
  },
  {
    feature: 'GEO / optimalizace pro AI vyhledávače (ChatGPT, Perplexity, AI Overviews)',
    cells: {
      us: { s: 'yes', t: 'v ceně' },
      mm: { s: 'partial', t: 'add-on +499 Kč' },
      collabim: { s: 'no' },
      mangools: { s: 'partial', t: 'AI Search Watcher' },
    },
  },
  {
    feature: 'Crawl celého webu (technický audit)',
    cells: {
      us: { s: 'yes', t: '19 kontrol' },
      mm: { s: 'yes' },
      collabim: { s: 'partial' },
      mangools: { s: 'no' },
    },
  },
  {
    feature: 'Sledování pozic včetně Seznam.cz',
    cells: {
      us: { s: 'yes', t: 'Google CZ/COM, Seznam' },
      mm: { s: 'yes' },
      collabim: { s: 'yes', t: 'jádro, denně' },
      mangools: { s: 'partial', t: 'slabý Seznam' },
    },
  },
  {
    feature: 'Keyword research a data (hledanost, obtížnost)',
    cells: {
      us: { s: 'partial', t: 'základ' },
      mm: { s: 'yes', t: 'silná stránka' },
      collabim: { s: 'yes' },
      mangools: { s: 'yes' },
    },
  },
  {
    feature: 'Analýza zpětných odkazů (backlinks)',
    cells: {
      us: { s: 'partial', t: 'přes externí data' },
      mm: { s: 'partial' },
      collabim: { s: 'partial' },
      mangools: { s: 'yes', t: 'LinkMiner' },
    },
  },
  {
    feature: 'Vícejazyčné rozhraní (12 jazyků)',
    cells: {
      us: { s: 'yes' },
      mm: { s: 'no', t: 'CZ/EN' },
      collabim: { s: 'no', t: 'CZ' },
      mangools: { s: 'partial', t: 'EN' },
    },
  },
  {
    feature: 'Veřejné API',
    cells: {
      us: { s: 'yes' },
      mm: { s: 'yes' },
      collabim: { s: 'partial' },
      mangools: { s: 'no' },
    },
  },
  {
    feature: 'Použití zdarma bez registrace',
    cells: {
      us: { s: 'yes', t: '20 analýz/den' },
      mm: { s: 'partial', t: 'free tarif' },
      collabim: { s: 'partial', t: 'free tarif' },
      mangools: { s: 'partial', t: 'free tarif' },
    },
  },
]

// V čem jsme lepší — vypíchnuté výhody
export const OUR_STRENGTHS: { title: string; desc: string }[] = [
  { title: 'Nejširší sada okamžitých nástrojů', desc: '26 technických a on-page nástrojů na jednom místě, bez instalace a bez registrace. Konkurence cílí hlavně na klíčová slova a pozice; technický audit je u nich vedlejší.' },
  { title: 'Reálný Lighthouse, CrUX a Safe Browsing', desc: 'Skutečná data od Googlu — výkon, Core Web Vitals od reálných návštěvníků i kontrola bezpečnosti. To konkurence neukazuje.' },
  { title: 'GEO / AI viditelnost v ceně', desc: 'Připravenost pro AI vyhledávače (ChatGPT, Perplexity, Google AI) máte rovnou v ceně — jinde je to placený add-on, nebo vůbec.' },
  { title: 'Nejnižší vstupní cena', desc: 'Placený tarif od 490 Kč/měsíc s neomezenými analýzami — levnější než hlavní čeští konkurenti.' },
  { title: '12 jazyků', desc: 'Rozhraní v 12 jazycích — využití i mimo český trh, kam zavedení konkurenti necílí.' },
]

// Kde je konkurence silnější — poctivě
export const THEIR_STRENGTHS: { title: string; desc: string }[] = [
  { title: 'Hloubka keyword dat', desc: 'Marketing Miner i Collabim mají roky budované databáze hledanosti a obtížnosti pro český trh. Pokud je vaše priorita rozsáhlý keyword research, mají náskok.' },
  { title: 'Backlinkový index', desc: 'Na hloubkovou analýzu zpětných odkazů je silnější Mangools (LinkMiner) nebo zahraniční Ahrefs.' },
  { title: 'Zralost a zavedenost', desc: 'Collabim je „nejpoužívanější SEO nástroj v ČR", Marketing Miner je dlouholetá značka s velkou komunitou.' },
]

export const COMPARE_VERIFIED = 'k červnu 2026'

// Jazykové overlaye — stejný vzor jako featurePages. cs je základ, ostatní jazyky
// doplňují přeložené texty (origin/freeTier, feature, cell.t, strengths, verified).
import cdEn from './comparisonData.en.json'
import cdDe from './comparisonData.de.json'
import cdPl from './comparisonData.pl.json'
import cdSk from './comparisonData.sk.json'
import cdIt from './comparisonData.it.json'
import cdHu from './comparisonData.hu.json'
import cdUk from './comparisonData.uk.json'
import cdEs from './comparisonData.es.json'
import cdFr from './comparisonData.fr.json'
import cdNl from './comparisonData.nl.json'
import cdJa from './comparisonData.ja.json'

const OVERLAYS: Record<string, any> = { en: cdEn, de: cdDe, pl: cdPl, sk: cdSk, it: cdIt, hu: cdHu, uk: cdUk, es: cdEs, fr: cdFr, nl: cdNl, ja: cdJa }

export interface ComparisonData {
  COMPETITORS: Competitor[]
  COMPARE_ROWS: CompareRow[]
  OUR_STRENGTHS: { title: string; desc: string }[]
  THEIR_STRENGTHS: { title: string; desc: string }[]
  COMPARE_VERIFIED: string
}

// Vrátí lokalizovanou variantu srovnání podle jazyka, s fallbackem na cs.
export function getComparisonData(lang: string): ComparisonData {
  const ov = OVERLAYS[lang] || (lang !== 'cs' ? OVERLAYS.en : undefined)
  if (!ov) return { COMPETITORS, COMPARE_ROWS, OUR_STRENGTHS, THEIR_STRENGTHS, COMPARE_VERIFIED }

  const competitors: Competitor[] = COMPETITORS.map((c) => ({ ...c, ...(ov.competitors?.[c.key] || {}) }))

  const rows: CompareRow[] = COMPARE_ROWS.map((row, i) => {
    const orow = ov.rows?.[i]
    if (!orow) return row
    const cells: Record<string, Cell> = {}
    for (const k of Object.keys(row.cells)) {
      const base = row.cells[k]
      const tr = orow.cells?.[k]
      cells[k] = tr !== undefined ? { ...base, t: tr } : base
    }
    return { feature: orow.feature ?? row.feature, cells }
  })

  return {
    COMPETITORS: competitors,
    COMPARE_ROWS: rows,
    OUR_STRENGTHS: ov.ourStrengths || OUR_STRENGTHS,
    THEIR_STRENGTHS: ov.theirStrengths || THEIR_STRENGTHS,
    COMPARE_VERIFIED: ov.verified || COMPARE_VERIFIED,
  }
}
